import { dbManager } from './db_manager';

// ── Achievement definitions ───────────────────────────────────────────────────

export type AchievementKey =
    | 'explorador_literario'
    | 'rato_biblioteca'
    | 'mestre_vocabulario'
    | 'inabalavel'
    | 'critico_literario'
    | 'anfitriao'
    | 'socio_clube'
    | 'madrugador'
    | 'coruja_noturna'
    | 'maratonista'
    | 'poliglota_foco'
    | 'colecionador_cards'
    | 'ouvido_atento'
    | 'primeiro_passo'
    | 'influenciador';

export interface AchievementDef {
    key: AchievementKey;
    name: string;
    lucideIcon: string;       // icon name to resolve at render time
    thresholds: [number, number, number, number, number]; // 5 levels
    unit: string;             // e.g. "livros", "dias", "palavras"
    description: string;
}

export const ACHIEVEMENTS: AchievementDef[] = [
    {
        key: 'explorador_literario',
        name: 'Explorador Literário',
        lucideIcon: 'Library',
        thresholds: [1, 5, 10, 30, 50],
        unit: 'livros lidos',
        description: 'Leia livros pela plataforma',
    },
    {
        key: 'rato_biblioteca',
        name: 'Rato de Biblioteca',
        lucideIcon: 'BookMarked',
        thresholds: [5, 10, 20, 50, 100],
        unit: 'livros salvos',
        description: 'Salve livros na sua estante',
    },
    {
        key: 'mestre_vocabulario',
        name: 'Mestre do Vocabulário',
        lucideIcon: 'Languages',
        thresholds: [50, 200, 500, 1000, 2000],
        unit: 'palavras marcadas',
        description: 'Marque palavras ao ler',
    },
    {
        key: 'inabalavel',
        name: 'Inabalável',
        lucideIcon: 'Flame',
        thresholds: [3, 7, 14, 30, 100],
        unit: 'dias seguidos',
        description: 'Leia em dias consecutivos',
    },
    {
        key: 'critico_literario',
        name: 'Crítico Literário',
        lucideIcon: 'MessageSquareText',
        thresholds: [1, 5, 10, 20, 50],
        unit: 'comentários',
        description: 'Comente no Clube do Livro',
    },
    {
        key: 'anfitriao',
        name: 'Anfitrião',
        lucideIcon: 'Crown',
        thresholds: [1, 3, 5, 10, 20],
        unit: 'clubes criados',
        description: 'Crie Clubes do Livro',
    },
    {
        key: 'socio_clube',
        name: 'Sócio do Clube',
        lucideIcon: 'Users',
        thresholds: [1, 3, 5, 10, 20],
        unit: 'clubes participados',
        description: 'Participe de Clubes do Livro',
    },
    {
        key: 'madrugador',
        name: 'Madrugador',
        lucideIcon: 'Sunrise',
        thresholds: [5, 10, 20, 40, 80],
        unit: 'capítulos antes das 8h',
        description: 'Leia antes das 8h da manhã',
    },
    {
        key: 'coruja_noturna',
        name: 'Coruja Noturna',
        lucideIcon: 'Moon',
        thresholds: [5, 10, 20, 40, 80],
        unit: 'capítulos após as 23h',
        description: 'Leia após as 23h',
    },
    {
        key: 'maratonista',
        name: 'Maratonista',
        lucideIcon: 'Timer',
        thresholds: [50, 100, 200, 400, 800],
        unit: 'páginas em um dia',
        description: 'Leia muitas páginas em um único dia',
    },
    {
        key: 'poliglota_foco',
        name: 'Poliglota em Foco',
        lucideIcon: 'Globe',
        thresholds: [100, 250, 500, 1000, 2000],
        unit: 'traduções',
        description: 'Traduza palavras enquanto lê',
    },
    {
        key: 'colecionador_cards',
        name: 'Colecionador de Cards',
        lucideIcon: 'Copy',
        thresholds: [20, 100, 300, 600, 1000],
        unit: 'flashcards criados',
        description: 'Crie flashcards de vocabulário',
    },
    {
        key: 'ouvido_atento',
        name: 'Ouvido Atento',
        lucideIcon: 'Headphones',
        thresholds: [10, 50, 150, 400, 1000], // áudios ouvidos no leitor (palavra, frase ou página)
        unit: 'áudios',
        description: 'Ouça palavras e frases durante a leitura',
    },
    {
        key: 'primeiro_passo',
        name: 'Primeiro Passo',
        lucideIcon: 'UserCheck',
        thresholds: [1, 2, 3, 4, 5],
        unit: 'perfil completo',
        description: 'Complete seu perfil (avatar, bio, username…)',
    },
    {
        key: 'influenciador',
        name: 'Influenciador',
        lucideIcon: 'Heart',
        thresholds: [10, 50, 100, 500, 1000],
        unit: 'reações recebidas',
        description: 'Receba reações emoji nos seus comentários',
    },
];

// ── Data types ────────────────────────────────────────────────────────────────

export interface UserAchievement {
    def: AchievementDef;
    level: number;       // 0–5
    progress: number;    // absolute value
}

// ── Level from progress ───────────────────────────────────────────────────────

export function getLevelFromProgress(def: AchievementDef, progress: number): number {
    let level = 0;
    for (let i = 0; i < 5; i++) {
        if (progress >= def.thresholds[i]) level = i + 1;
    }
    return level;
}

export function getNextThreshold(def: AchievementDef, level: number): number | null {
    if (level >= 5) return null;
    return def.thresholds[level]; // index = next threshold (since level 1 = thresholds[0])
}

// ── Load user achievements from D1 ───────────────────────────────────────────

export async function loadAchievements(userId: string): Promise<UserAchievement[]> {
    try {
        const results = await dbManager.from('user_achievements', userId).select();
        const data = results || [];

        const rowMap = new Map<string, { level: number; progress: number }>();
        data.forEach((r: any) => rowMap.set(r.achievement_key, r));

        return ACHIEVEMENTS.map(def => {
            const row = rowMap.get(def.key);
            return {
                def,
                level: row?.level ?? 0,
                progress: row?.progress ?? 0,
            };
        });
    } catch (err) {
        console.error('[achievements] load error:', err);
        return ACHIEVEMENTS.map(def => ({ def, level: 0, progress: 0 }));
    }
}

// ── Sync with the server ──────────────────────────────────────────────────────
// O servidor calcula o progresso real de cada conquista a partir do D1, soma os contadores
// (Madrugador, Coruja Noturna) e grava com ON CONFLICT(user_id, achievement_key).

type LevelUp = { key: AchievementKey; newLevel: number };
type SyncData = { set?: Partial<Record<AchievementKey, number>>; increments?: Partial<Record<AchievementKey, number>> };

async function syncOnServer(userId: string, data: SyncData = {}): Promise<LevelUp[]> {
    if (!userId || userId === 'guest') return [];
    try {
        const res: any = await dbManager.execute({ action: 'sync_achievements', table: 'user_achievements', user_id: userId, data } as any);
        try { localStorage.removeItem(`libritui_cached_achievements_${userId}`); } catch {}
        return res && Array.isArray(res.levelUps) ? res.levelUps : [];
    } catch (err) {
        console.error('[achievements] sync error:', err);
        return [];
    }
}

/** Recalcula todas as conquistas a partir da atividade real e devolve as que subiram de nível. */
export async function syncAchievements(userId: string): Promise<LevelUp[]> {
    return syncOnServer(userId);
}

/** Uma vez por sessão, ao abrir o app: libera de forma retroativa o que o usuário já fez. */
export async function syncAchievementsOncePerSession(userId: string): Promise<LevelUp[]> {
    const key = `libritui_achievements_synced_${userId}`;
    try {
        if (sessionStorage.getItem(key)) return [];
        sessionStorage.setItem(key, '1');
    } catch {}
    return syncOnServer(userId);
}

/** Grava um novo valor absoluto (só se for maior que o atual). */
export async function trackAchievement(
    userId: string,
    key: AchievementKey,
    newProgress: number
): Promise<{ levelUp: boolean; newLevel: number } | null> {
    const up = (await syncOnServer(userId, { set: { [key]: newProgress } })).find(u => u.key === key);
    return up ? { levelUp: true, newLevel: up.newLevel } : null;
}

/** Soma `delta` ao progresso atual. */
export async function incrementAchievement(
    userId: string,
    key: AchievementKey,
    delta: number = 1
): Promise<{ levelUp: boolean; newLevel: number } | null> {
    const up = (await syncOnServer(userId, { increments: { [key]: delta } })).find(u => u.key === key);
    return up ? { levelUp: true, newLevel: up.newLevel } : null;
}

/** Conta uma manhã (antes das 8h) ou noite (depois das 23h) de leitura, no máximo uma vez por dia. */
export async function trackReadingHour(userId: string): Promise<void> {
    const hour = new Date().getHours();
    const key: AchievementKey | null = hour < 8 ? 'madrugador' : hour >= 23 ? 'coruja_noturna' : null;
    if (!key || !userId) return;
    const dayKey = `libritui_reading_hour_${key}_${new Date().toISOString().slice(0, 10)}`;
    try {
        if (localStorage.getItem(dayKey)) return;
        localStorage.setItem(dayKey, '1');
    } catch { return; }
    await incrementAchievement(userId, key, 1);
}

// ── Contadores do leitor (traduções, áudios ouvidos) ──────────────────────────
// Agrupa os toques e envia ao servidor alguns segundos depois, numa chamada só.
const pending: Partial<Record<AchievementKey, number>> = {};
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let pendingUser = '';

export function queueAchievementIncrement(userId: string, key: AchievementKey, delta: number = 1): void {
    if (!userId || userId === 'guest') return;
    if (pendingUser && pendingUser !== userId) Object.keys(pending).forEach(k => delete pending[k as AchievementKey]);
    pendingUser = userId;
    pending[key] = (pending[key] || 0) + delta;
    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = setTimeout(() => {
        const increments = { ...pending };
        Object.keys(pending).forEach(k => delete pending[k as AchievementKey]);
        flushTimer = null;
        syncOnServer(userId, { increments }).catch(() => {});
    }, 4000);
}
